import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { faBell, faCalendar, faChevronDown, faRepeat, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Task } from '../../types';
import { isStartSchedule } from '../../utils/taskSchedule';
import { TaskRecurrenceModal } from '../task-recurrence-modal/TaskRecurrenceModal';
import { TaskRecurrenceQuickModal } from '../task-recurrence-modal/TaskRecurrenceQuickModal';
import '../add-task/AddTask.css';

interface TaskScheduleModalProps {
    dueDate: Dayjs | null;
    schedule?: Task['schedule'];
    reminderTime: Dayjs | null;
    recurrence?: Task['recurrence'];
    onDueDateChange: (value: Dayjs | null) => void;
    onScheduleChange: (schedule?: Task['schedule']) => void;
    onReminderTimeChange: (value: Dayjs | null) => void;
    onRecurrenceChange: (recurrence?: Task['recurrence']) => void;
    onClose: () => void;
}

export const TaskScheduleModal: React.FC<TaskScheduleModalProps> = ({
    dueDate, schedule, reminderTime, recurrence, onDueDateChange, onScheduleChange, onReminderTimeChange, onRecurrenceChange, onClose,
}) => {
    const { t, i18n } = useTranslation();
    const [showRecurrenceOptions, setShowRecurrenceOptions] = useState(false);
    const [showCustomRecurrence, setShowCustomRecurrence] = useState(false);
    const [showMoreOptions, setShowMoreOptions] = useState(Boolean(reminderTime || recurrence));
    const [openPicker, setOpenPicker] = useState<'start' | 'end' | 'reminder' | null>(null);
    const startDateRef = useRef<HTMLButtonElement>(null);
    const endDateRef = useRef<HTMLButtonElement>(null);
    const reminderRef = useRef<HTMLButtonElement>(null);
    const formatDate = (value: Dayjs) => value.toDate().toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    const [rangePrecision, setRangePrecision] = useState<NonNullable<Task['schedule']>['precision']>(schedule?.precision || 'day');
    const normalizeRangeDate = (value: Dayjs, precision = rangePrecision) => precision === 'month'
        ? value.startOf('month') : precision === 'year' ? value.startOf('year') : precision === 'hour' ? value : value.startOf('day');
    const formatRangeDate = (value: Dayjs) => rangePrecision === 'year'
        ? value.format('YYYY') : rangePrecision === 'month'
            ? value.toDate().toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : formatDate(value);
    const formatDateTime = (value: Dayjs) => value.toDate().toLocaleString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    });
    // Older tasks have no kind. They were created as deadline-only tasks, so
    // preserve that interpretation while letting new tasks use a unified form.
    const hasStart = isStartSchedule(schedule) || schedule?.kind === 'range';
    const hasEnd = (!!dueDate && !hasStart) || schedule?.kind === 'range';
    const endDate = schedule?.kind === 'range' && schedule.endDate ? dayjs(schedule.endDate) : dueDate;
    const normalizeScheduleValue = (value: Dayjs) => rangePrecision === 'hour' ? value : normalizeRangeDate(value);
    const formatScheduleValue = (value: Dayjs) => rangePrecision === 'hour' ? formatDateTime(value) : formatRangeDate(value);
    const getValidRangeEnd = (start: Dayjs, end: Dayjs) => {
        if (rangePrecision === 'hour' && !end.isAfter(start)) return start.add(1, 'hour');
        return end.isBefore(start) ? start : end;
    };
    const getMovedRangeEnd = (previousStart: Dayjs, previousEnd: Dayjs, start: Dayjs) => (
        getValidRangeEnd(start, start.add(previousEnd.diff(previousStart), 'millisecond'))
    );
    const getMovedRangeStart = (previousStart: Dayjs, previousEnd: Dayjs, end: Dayjs) => (
        end.subtract(previousEnd.diff(previousStart), 'millisecond')
    );
    const openCustomRecurrence = () => {
        if (!recurrence) {
            onRecurrenceChange({
                unit: 'week',
                interval: 1,
                mode: 'fixed',
                pattern: 'calendar',
                calendarDays: [new Date().getDay()],
            });
        }
        setShowRecurrenceOptions(false);
        setShowCustomRecurrence(true);
    };
    const clearSchedule = () => {
        onDueDateChange(null);
        onScheduleChange(undefined);
        if (recurrence) onRecurrenceChange(undefined);
    };
    const handleStartDateChange = (value: Dayjs | null) => {
        if (!value) return clearStartDate();
        const start = normalizeScheduleValue(value);
        if (hasEnd && !hasStart && dueDate) {
            const oldEnd = dueDate;
            onDueDateChange(start);
            const end = getValidRangeEnd(start, oldEnd);
            onScheduleChange({ precision: rangePrecision, kind: 'range', startDate: start.toISOString(), endDate: end.toISOString() });
            return;
        }
        onDueDateChange(start);
        const currentEnd = schedule?.endDate ? normalizeScheduleValue(dayjs(schedule.endDate)) : start;
        const previousStart = dueDate ? normalizeScheduleValue(dueDate) : start;
        const end = hasStart && hasEnd && start.isAfter(currentEnd)
            ? getMovedRangeEnd(previousStart, currentEnd, start)
            : getValidRangeEnd(start, currentEnd);
        onScheduleChange(hasEnd
            ? { precision: rangePrecision, kind: 'range', startDate: start.toISOString(), endDate: end.toISOString() }
            : { precision: rangePrecision, kind: 'start', startDate: start.toISOString() });
    };
    const clearStartDate = () => {
        if (!hasStart) return;
        if (hasEnd && endDate) {
            onDueDateChange(endDate);
            onScheduleChange({ precision: rangePrecision, kind: 'deadline', endDate: endDate.toISOString() });
        } else clearSchedule();
    };
    const handleEndDateChange = (value: Dayjs | null) => {
        if (!value) return clearEndDate();
        if (hasStart && dueDate) {
            const start = normalizeScheduleValue(dueDate);
            const end = normalizeScheduleValue(value);
            const currentEnd = schedule?.endDate ? normalizeScheduleValue(dayjs(schedule.endDate)) : start;
            const rangeStart = end.isBefore(start) ? getMovedRangeStart(start, currentEnd, end) : start;
            onDueDateChange(rangeStart);
            const rangeEnd = getValidRangeEnd(rangeStart, end);
            onScheduleChange({
                precision: rangePrecision,
                kind: 'range',
                startDate: rangeStart.toISOString(),
                endDate: rangeEnd.toISOString(),
            });
            return;
        }
        onDueDateChange(value);
        onScheduleChange({ precision: rangePrecision, kind: 'deadline', endDate: value.toISOString() });
    };
    const clearEndDate = () => {
        if (!hasEnd) return;
        if (hasStart && dueDate) onScheduleChange({ precision: rangePrecision, kind: 'start', startDate: dueDate.toISOString() });
        else clearSchedule();
    };
    const handleRangePrecisionChange = (precision: NonNullable<Task['schedule']>['precision']) => {
        setRangePrecision(precision);
        if (!dueDate) return;
        const start = normalizeRangeDate(dueDate, precision);
        const normalizedEnd = schedule?.endDate ? normalizeRangeDate(dayjs(schedule.endDate), precision) : start;
        const currentEnd = precision === 'hour' && normalizedEnd.isSame(start)
            ? start.add(1, 'hour') : normalizedEnd;
        if (hasStart && hasEnd) {
            onDueDateChange(start);
            const end = precision === 'hour' && !currentEnd.isAfter(start)
                ? start.add(1, 'hour') : currentEnd.isBefore(start) ? start : currentEnd;
            onScheduleChange({ precision, kind: 'range', startDate: start.toISOString(), endDate: end.toISOString() });
        } else if (hasStart) {
            onDueDateChange(start);
            onScheduleChange({ precision, kind: 'start', startDate: start.toISOString() });
        } else {
            onDueDateChange(start);
            onScheduleChange({ precision, kind: 'deadline', endDate: start.toISOString() });
        }
    };
    const rangePickerProps = rangePrecision === 'year'
        ? { views: ['year'] as const, openTo: 'year' as const, format: 'YYYY' }
        : rangePrecision === 'month'
            ? { views: ['year', 'month'] as const, openTo: 'month' as const, format: 'MMM YYYY' }
            : { views: ['year', 'month', 'day'] as const, openTo: 'day' as const, format: 'DD MMM YYYY' };
    const recurrenceSummary = () => {
        if (!recurrence) return t('addTask.doesNotRepeat');

        const unit = recurrence.interval === 1 ? t(`addTask.${recurrence.unit}`) : t(`addTask.${recurrence.unit}s`);
        const intervalSummary = recurrence.interval === 1
            ? t('addTask.recurrenceEveryUnit', { unit })
            : t('addTask.recurrenceIntervalSummary', { count: recurrence.interval, unit });
        let summary: string;

        if (recurrence.pattern === 'calendar') {
            if (recurrence.businessDays) {
                summary = t('addTask.recurrenceBusinessDaysSummary');
            } else {
                const weekdayLabels = i18n.language.startsWith('ro')
                    ? ['D', 'L', 'M', 'M', 'J', 'V', 'S']
                    : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
                const days = recurrence.unit === 'week'
                    ? [1, 2, 3, 4, 5, 6, 0].filter((day) => recurrence.calendarDays?.includes(day)).map((day) => weekdayLabels[day]).join(', ')
                    : recurrence.calendarDays?.join(', ');
                summary = t(recurrence.unit === 'week' ? 'addTask.recurrenceWeekSummary' : 'addTask.recurrenceMonthSummary', { days });
            }
            if (recurrence.interval > 1) summary = `${summary}, ${intervalSummary}`;
        } else if (recurrence.mode === 'completion') {
            summary = t('addTask.recurrenceCompletionSummary', { count: recurrence.interval, unit });
        } else if (recurrence.interval === 1) {
            summary = t(`addTask.${recurrence.unit === 'day' ? 'daily' : recurrence.unit === 'week' ? 'weekly' : recurrence.unit === 'month' ? 'monthly' : 'yearly'}`);
        } else {
            summary = t('addTask.recurrenceIntervalSummary', { count: recurrence.interval, unit });
        }

        if (recurrence.endDate) return `${summary} · ${t('addTask.endsOn')} ${formatDate(dayjs(recurrence.endDate))}`;
        return recurrence.occurrencesRemaining
            ? `${summary} · ${t('addTask.recurrenceAfterOccurrencesSummary', { count: recurrence.occurrencesRemaining })}`
            : summary;
    };

    return <>
        {createPortal(
            <div className="task-schedule-modal-overlay" onClick={onClose}>
                <div className="task-schedule-modal" onClick={(event) => event.stopPropagation()}>
                    <h4>{t('addTask.schedule')}</h4>
                    <div className="task-schedule-kind"><span>{t('addTask.when')}</span><select value={rangePrecision} onChange={(event) => handleRangePrecisionChange(event.target.value as NonNullable<Task['schedule']>['precision'])}><option value="hour">{t('addTask.dateAndTime')}</option><option value="day">{t('addTask.day')}</option><option value="month">{t('addTask.month')}</option><option value="year">{t('addTask.year')}</option></select></div>
                    {hasStart ? (
                        <div className="task-schedule-field">
                            <span>{t('addTask.availableFrom')}</span>
                            <div className="task-schedule-value-group set">
                                <button
                                    ref={startDateRef}
                                    type="button"
                                    className="task-schedule-value"
                                    onClick={() => setOpenPicker('start')}
                                >
                                    <FontAwesomeIcon icon={faCalendar} />
                                    {dueDate && formatScheduleValue(dueDate)}
                                </button>
                                <button
                                    type="button"
                                    className="task-schedule-inline-remove"
                                    onClick={clearStartDate}
                                    aria-label={t('addTask.removeStartDate')}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            ref={startDateRef}
                            type="button"
                            className="task-schedule-add-start"
                            onClick={() => setOpenPicker('start')}
                        >
                            + {t('addTask.addStartDate')}
                        </button>
                    )}
                    {rangePrecision !== 'hour'
                        ? (
                            <DatePicker
                                open={openPicker === 'start'}
                                onClose={() => setOpenPicker(null)}
                                value={hasStart ? dueDate : null}
                                onChange={(value) => {
                                    handleStartDateChange(value);
                                    setOpenPicker(null);
                                }}
                                {...rangePickerProps}
                                slots={{ textField: () => null }}
                                slotProps={{
                                    popper: { anchorEl: startDateRef.current },
                                    actionBar: { actions: ['clear', 'accept'] },
                                }}
                            />
                        ) : (
                            <DateTimePicker
                                open={openPicker === 'start'}
                                onClose={() => setOpenPicker(null)}
                                value={hasStart ? dueDate : null}
                                onChange={(value) => {
                                    handleStartDateChange(value);
                                    setOpenPicker(null);
                                }}
                                ampm={false}
                                timeSteps={{ minutes: 1 }}
                                slots={{ textField: () => null }}
                                slotProps={{
                                    popper: { anchorEl: startDateRef.current },
                                    actionBar: { actions: ['clear', 'accept'] },
                                }}
                            />
                        )}
                    <div className="task-schedule-field">
                        <span>{t('addTask.availableUntil')}</span>
                        <div className={`task-schedule-value-group ${hasEnd ? 'set' : ''}`}>
                            <button
                                ref={endDateRef}
                                type="button"
                                className="task-schedule-value"
                                onClick={() => setOpenPicker('end')}
                            >
                                <FontAwesomeIcon icon={faCalendar} />
                                {hasEnd && endDate
                                    ? formatScheduleValue(endDate)
                                    : t('addTask.noEndDate')}
                            </button>
                            {hasEnd && (
                                <button
                                    type="button"
                                    className="task-schedule-inline-remove"
                                    onClick={clearEndDate}
                                    aria-label={t('addTask.removeDueDate')}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>
                    {rangePrecision !== 'hour'
                        ? (
                            <DatePicker
                                open={openPicker === 'end'}
                                onClose={() => setOpenPicker(null)}
                                value={hasEnd ? endDate : null}
                                onChange={(value) => {
                                    handleEndDateChange(value);
                                    setOpenPicker(null);
                                }}
                                {...rangePickerProps}
                                slots={{ textField: () => null }}
                                slotProps={{
                                    popper: { anchorEl: endDateRef.current },
                                    actionBar: { actions: ['clear', 'accept'] },
                                }}
                            />
                        ) : (
                            <DateTimePicker
                                open={openPicker === 'end'}
                                onClose={() => setOpenPicker(null)}
                                value={hasEnd ? endDate : null}
                                onChange={(value) => {
                                    handleEndDateChange(value);
                                    setOpenPicker(null);
                                }}
                                ampm={false}
                                timeSteps={{ minutes: 1 }}
                                slots={{ textField: () => null }}
                                slotProps={{
                                    popper: { anchorEl: endDateRef.current },
                                    actionBar: { actions: ['clear', 'accept'] },
                                }}
                            />
                        )}
                    {showMoreOptions ? <>
                        <div className="task-schedule-reminder">
                            <div className="task-schedule-field">
                                <div className="task-schedule-value-row">
                                    <button
                                        ref={reminderRef}
                                        type="button"
                                        className={`task-schedule-value ${reminderTime ? 'set' : ''}`}
                                        onClick={() => setOpenPicker('reminder')}
                                    >
                                        <FontAwesomeIcon icon={faBell} />
                                        {reminderTime ? formatDateTime(reminderTime) : t('addTask.noReminder')}
                                    </button>
                                    {reminderTime && (
                                        <button
                                            type="button"
                                            className="task-schedule-remove-value"
                                            onClick={() => onReminderTimeChange(null)}
                                            aria-label={t('addTask.noReminder')}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <DateTimePicker
                                open={openPicker === 'reminder'}
                                onClose={() => setOpenPicker(null)}
                                value={reminderTime}
                                onChange={(value) => {
                                    onReminderTimeChange(value);
                                    setOpenPicker(null);
                                }}
                                ampm={false}
                                timeSteps={{ minutes: 1 }}
                                disablePast
                                slots={{ textField: () => null }}
                                slotProps={{
                                    popper: { anchorEl: reminderRef.current },
                                    actionBar: { actions: ['clear', 'accept'] },
                                }}
                            />
                        </div>
                        {dueDate && (
                            <>
                                <button
                                    type="button"
                                    className={`task-schedule-recurrence ${recurrence ? 'active' : ''}`}
                                    onClick={() => setShowRecurrenceOptions(true)}
                                >
                                    <FontAwesomeIcon icon={faRepeat} />
                                    <span className="task-schedule-recurrence-label">
                                        {recurrenceSummary()}
                                    </span>
                                    <FontAwesomeIcon icon={faChevronDown} />
                                </button>
                                {recurrence && <label className="task-recurrence-checkbox task-schedule-copy-subtasks">
                                    <input type="checkbox" checked={!!recurrence.copySubtasks}
                                        onChange={(event) => onRecurrenceChange({
                                            ...recurrence,
                                            copySubtasks: event.target.checked,
                                        })} />
                                    {t('addTask.copySubtasksToNextOccurrence')}
                                </label>}
                            </>
                        )}
                    </> : <button type="button" className="task-schedule-more-options" onClick={() => setShowMoreOptions(true)}>{t('addTask.moreOptions')}</button>}
                    <button type="button" className="task-recurrence-modal-done" onClick={onClose}>{t('addTask.done')}</button>
                </div>
            </div>,
            document.body,
        )}
        {showRecurrenceOptions && (
            <TaskRecurrenceQuickModal
                onChange={onRecurrenceChange}
                onCustom={openCustomRecurrence}
                onClose={() => setShowRecurrenceOptions(false)}
            />
        )}
        {showCustomRecurrence && recurrence && (
            <TaskRecurrenceModal
                recurrence={recurrence}
                onChange={onRecurrenceChange}
                onClose={() => setShowCustomRecurrence(false)}
            />
        )}
    </>;
};
